<template>
  <div v-if="requestInfo.itemId" class="mx-3">
    <div class="row py-4 border-bottom">
      <div class="col">
        <div class="d-flex justify-content-start align-items-center">
          <icon-view
            class="w-auto"
            icon="fa fa-tasks-alt"
            icon-background="#fff"
            icon-color="#F3AA1C"
            variant="icon-view-size"
          />
          <span class="color-black-1' font-18 font-weight-bold">
            {{ $t('ApplicationInformation') }}
          </span>
        </div>
      </div>
      <div class="col d-flex justify-content-end mr-3">
        <action-button
          :text="$t('GoBack')"
          class="warning outline-warning px-4 py-2 mr-2"
          i="fa fa-arrow-circle-left"
          @click="goToHomeHandler"
        />

        <action-butto
          :text="$t('RemoveRequest')"
          class="btn-danger-outline px-4 py-2 pl-3 ml-3"
          i="fa fa-trash"
          @click="onOpenGenerateReportModal"
        />

        <action-button
          v-if="!isRequestReadOnly"
          :text="$t('Save')"
          class="btn-success-outline px-4 py-2 mr-2"
          i="fa fa-save"
          @click="onSaveHandler"
        />
        <action-button
          v-if="tabId === 5 && canEditLetterTab"
          id="btnSaveAuthorizationLetter"
          :text="$t('Save')"
          class="btn-success px-4 py-2 mr-2 mx-2"
          i="fa fa-save"
          @click="onSaveAuthorizationLetterHandler"
        />
        <action-button
          v-if="tabId === 5"
          :text="$t('printLetter')"
          class="btn-warning px-4 py-2 mx-2"
          i="fa fa-print"
          :disabled="isPrintingLetter"
          @click="onPrintAuthorizationLetter"
        />
        <action-button
          v-if="!isRequestReadOnly && isRequester"
          :text="$t('SubmitRequest')"
          class="btn-success px-4 py-2"
          i="fa fa-flag"
          @click="onSubmitRequestHandler"
        />

        <action-button
          v-if="isSupervisorOrAdmin"
          :text="$t('RemoveRequest')"
          class="btn-danger-outline px-4 py-2 pl-3 ml-3"
          i="fa fa-trash"
          @click="onRemoveRequestHandler"
        />

        <GenerateReportModal
          ref="generateReportModal"
          @close="onCloseGenerateReportModal"
          @accept="onOpenGenerateReportModal"
        />
      </div>
    </div>
    <div class="row bg-gray-21 rounded py-3 pl-3 mx-2 mt-3 mb-5">
      <div class="col-12 col-md-3">
        <div class="d-flex align-items-center">
          <em :class="`fa fa-circle color-${statusColor}`" />
          <div class="color-gray-18 font-14 ml-2">
            {{ requestInfo.requestStatusName }}
          </div>
        </div>
        <span class="color-primary-4 font-14 font-weight-bold ml-4">
          #{{ requestInfo.requestNumber }}
        </span>
      </div>
      <div class="col-12 col-md-3 mt-3 mt-md-0">
        <div class="color-gray-18 font-14">
          {{ $t('TypeHeader.HeaderText') }}:
        </div>
        <span class="color-black font-14">
          {{ requestInfo.requestTypeName }}
        </span>
      </div>
      <div class="col-12 col-md-3 mt-4 mt-md-0">
        <div class="color-gray-18 font-14">
          {{ $t('OccurredInHeader.HeaderText') }}:
        </div>
        <span class="color-black font-14">
          {{ requestInfo.requestDate | date }}
        </span>
      </div>
      <div class="col-12 col-md-3 mt-4 mt-md-0 d-flex justify-content-end">
        <action-button
          :text="$t('DownloadYourRequest')"
          class="warning px-4 py-2"
          i="fad fa-arrow-to-bottom"
          @click="onDownloadRequestReportHandler"
        />
      </div>
    </div>
    <div class="mt-4 mx-2">
      <custom-tab v-model="tabId" class="custom-tab__extended bg-gray-21">
        <custom-tab-item
          :active="tabId === 0"
          :lazy="false"
          :title="$t('ApplicantInformation')"
        >
          <div class="p-3 mt-3">
            <applicant-information
              ref="step1Ref"
              :is-read-only="true"
              :info="requestInfo.stepI"
            />
          </div>
        </custom-tab-item>
        <custom-tab-item :lazy="false" :title="$t('InvestigatorInformation')">
          <div class="p-3 mt-3">
            <responsible-information
              ref="step2Ref"
              :info="requestInfo.stepII"
              :is-read-only="isRequestReadOnly"
            />
          </div>
        </custom-tab-item>
        <custom-tab-item :lazy="false" :title="$t('InvestigationDataTitle')">
          <div class="p-3 mt-3">
            <research-information
              ref="step3Ref"
              :info="requestInfo.stepIII"
              :is-read-only="isRequestReadOnly"
              :is-edit="disableStepIII"
            />
          </div>
        </custom-tab-item>
        <custom-tab-item :lazy="false" :title="$t('ResearchObjectives')">
          <div class="p-3 mt-3">
            <research-objectives
              ref="step4Ref"
              :info="requestInfo.stepIV"
              :is-read-only="isRequestReadOnly"
            />
          </div>
        </custom-tab-item>
        <custom-tab-item
          :active="tabId === 4"
          :lazy="false"
          :title="$t('AttachDocuments')"
        >
          <div class="p-3 mt-3">
            <document-validation
              ref="step5Ref"
              :item-id="requestInfo.itemId"
              :info="requestInfo.stepV"
              :is-read-only="isRequestReadOnly"
              :is-new-request="false"
              @on-refresh-document="onRefreshHandler"
            />
          </div>
        </custom-tab-item>
        <custom-tab-item
          :active="tabId === 5"
          :lazy="true"
          :title="'Carta de Autorización'"
          :nav-item-class="{ 'd-none': !shouldShowLetterTab }"
        >
          <div class="p-3 mt-3" v-if="shouldShowLetterTab">
            <AuthorizationLetter
              ref="authorizationLetter"
              :is-read-only="!canEditLetterTab"
            />
          </div>
        </custom-tab-item>
      </custom-tab>
    </div>
    <!--    Rating Survey Modal-->
    <rating-modal
      ref="myRatingSurveyRef"
      @close="onCloseRatingSurveyModal"
      @accept="onAcceptRatingSurveyHandler"
    />
  </div>
</template>

<script>
//libs
import { mapMutations, mapState, mapGetters } from 'vuex';
import { name as userModuleName } from '@/store/modules/user';
import roles from '@/utils/constants/roles.js';

import { CustomTab, CustomTabItem } from 'custom-tabs';
import IconView from 'icon-view';
import ActionButton from 'ActionButton';

//services
import {
  getRequest as _getRequest,
  saveRequest as _saveRequest,
  submitRequest as _submitRequest,
  saveRatingSurvey as _saveRatingSurvey,
  removeRequest as _removeRequest,
} from '@/services/app.api.js';
import { getUserProfile } from '@/services/auth.api.js';
import { i18n } from '@/i18n';

//components
import {
  ApplicantInformation,
  DocumentValidation,
  ResearchInformation,
  ResearchObjectives,
  ResponsibleInformation,
  AuthorizationLetter,
} from './steps';
import RatingModal from './RatingModal.vue';

//constants
import requestStatus, {
  requestStatusColor,
} from '@/utils/constants/requestStatus.js';
import requestSchoolTypes from '@/utils/constants/requestSchoolTypes';
import attachmentCategories from '@/utils/constants/attachmentCategories';
import caseNotifyTypes from '@/utils/constants/caseNotifyType';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import {
  getInvestigationRequestReport,
  getAuthorizationLetterReport,
} from '@/services/report.api';
import { convertToBlob } from '@/utils/utilities.js';

//vuex types
import types from '@/store/modules/app/types.js';
import { name as appModuleName } from '@/store/modules/app';

//TODO: (Move the component and instance to the right place)
import GenerateReportModal from '@/views/request/GenerateReportModal.vue';

export default {
  name: 'EditCase',
  mixins: [alertMixin],
  components: {
    IconView,
    CustomTab,
    CustomTabItem,
    ActionButton,
    ApplicantInformation,
    DocumentValidation,
    ResearchInformation,
    ResearchObjectives,
    ResponsibleInformation,
    RatingModal,
    AuthorizationLetter,
    GenerateReportModal,
  },
  watch: {
    lastNotifyRefreshCaseInfo(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (
          this.lastNotifyRefreshCaseInfoType ===
          caseNotifyTypes.REFRESH_CASE_INFO
        ) {
          this.getRequestInfo();
        }
      }
    },
    shouldShowLetterTab(visible) {
      if (!visible && this.tabId === 5) {
        this.tabId = 0;
      }
    },
  },
  data: () => ({
    isRequester: false,
    tabId: null,
    requestInfo: {},
    applicantInfo: {},
    disableStepIII: true,
    isPrintingLetter: false,
  }),
  computed: {
    ...mapState(appModuleName, [
      'lastNotifyRefreshCaseInfo',
      'lastNotifyRefreshCaseInfoType',
      'lastNotifyRefreshCaseInfoValue',
    ]),
    caseNotifyTypeEnum() {
      return caseNotifyTypes;
    },
    businessId() {
      return this.$route.params.businessId;
    },
    itemId() {
      return this.requestInfo?.itemId;
    },
    statusColor() {
      return requestStatusColor[this.requestInfo?.requestStatusId];
    },
    isRequestReadOnly() {
      return (
        [
          requestStatus.Sometida,
          requestStatus.EnProceso,
          requestStatus.DocumentoCarta,
          requestStatus.RedaccionCarta,
          requestStatus.Autorizado,
          requestStatus.DocumentoTesis,
        ].includes(this.requestInfo?.requestStatusId) || this.isCaseResolved
      );
    },
    canEditLetterTab() {
      return this.requestInfo?.requestStatusId === requestStatus.RedaccionCarta;
    },
    isSupervisor() {
      return this.currentRole === roles.SUPERVISOR.toLowerCase();
    },
    isSupervisorOrAdmin() {
      return (
        this.currentRole === roles.SUPERVISOR.toLowerCase() ||
        this.currentRole === roles.ADMIN.toLowerCase()
      );
    },
    currentRole() {
      return this.roleName()?.toLowerCase();
    },
    shouldShowLetterTab() {
      return this.isSupervisorOrAdmin && this.canEditLetterTab;
    },
  },
  async mounted() {
    await this.getRequestInfo();
    this.isRequester = this.currentRole === roles.APPLICANT.toLowerCase();
    //Get letter information
    if (this.$refs.authorizationLetter) {
      await this.$refs.authorizationLetter.getLetter();
    }
  },
  methods: {
    ...mapMutations(appModuleName, [
      types.SET_SITEMAP_INFO,
      types.SET_ITEM_MODEL,
    ]),
    ...mapGetters(userModuleName, {
      roleName: 'role',
    }),
    async getRequestInfo() {
      if (this.businessId) {
        this.requestInfo = (await _getRequest(this.businessId)).data;

        try {
          this.applicantInfo = (
            await getUserProfile(this.requestInfo.ownerId)
          )?.data;
        } catch (e) {
          console.log('Error loading user profile:', e);
        }
        this.disableStepIII = !(
          this.requestInfo.requestStatusId == requestStatus.Nuevo ||
          this.requestInfo.requestStatusId ==
            requestStatus.DevueltoPorFaltaInformacion
        );
        this.initializeSitemapInfo();
        this[types.SET_ITEM_MODEL](this.requestInfo);
      }
    },
    initializeSitemapInfo() {
      this[types.SET_SITEMAP_INFO]({
        routes: [
          i18n.t('Home'),
          `${i18n.t('ApplicationHeader.HeaderText')} #${this.businessId}`,
        ],
        request: {
          requestStatusId: this.requestInfo?.requestStatusId,
          requestStatusName: this.requestInfo?.requestStatusName,
          requestNumber: this.requestInfo?.requestNumber,
        },
      });
    },
    goToHomeHandler() {
      this.$router.replace('/');
    },
    goToAuthorizationLetterHandler() {
      this.$router.push({
        name: 'AuthorizedLetter',
        params: {
          businessId: this.businessId,
        },
      });
    },
    async validateAllSteps(skipDocumentValidation = true) {
      const isStep2Valid = await this.$refs.step2Ref.validate();
      const isStep3Valid = await this.$refs.step3Ref.validate();
      const isStep4Valid = await this.$refs.step4Ref.validate();
      const isStep5Valid = await this.$refs.step5Ref.validate();

      const areAllValidated = [isStep2Valid, isStep3Valid, isStep4Valid].every(
        (x) => x === true
      );

      if (!areAllValidated) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }

      if (!skipDocumentValidation) {
        if (!isStep5Valid) {
          this.ShowToast('', i18n.t('StatusChange-DocumentsError'), 'error');
          return;
        }
      }

      return true;
    },
    async onSaveHandler() {
      const isValid = await this.validateAllSteps();
      if (!isValid) return;

      let saveResponse = await this.onSave();

      if (saveResponse) {
        this.ShowToast(
          'Proceso exitoso',
          'El caso ha sido actualizado exitosamente.',
          'success'
        );
        await this.getRequestInfo();
      }
    },
    async onSaveAuthorizationLetterHandler() {
      this.$refs.authorizationLetter.onSaveHandler();
    },
    async onPrintAuthorizationLetter() {
      if (this.isPrintingLetter) return;
      if (this.businessId) {
        this.isPrintingLetter = true;
        try {
          const response = await getAuthorizationLetterReport(
            this.businessId,
            this.isEnLang
          ).catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });
          if (response?.data) {
            convertToBlob(response.data);
          }
        } finally {
          this.isPrintingLetter = false;
        }
      }
    },
    async onSave() {
      const modelStep1 = this.$refs.step1Ref.getModel();
      const modelStep2 = this.$refs.step2Ref.getModel();
      const modelStep3 = this.$refs.step3Ref.getModel();
      const modelStep4 = this.$refs.step4Ref.getModel();
      const modelStep5 = this.$refs.step5Ref.getModel();

      const payload = {
        id: this.requestInfo.itemId,
        //step 1
        fullName: modelStep1?.fullName,
        fatherLastName: modelStep1?.fatherLastName,

        //step 2
        mentorFullName: modelStep2?.mentorFullName,
        mentorTelephone: modelStep2?.mentorTelephone,
        mentorWorkPlace: modelStep2?.mentorWorkPlace,
        mentorEmail: modelStep2?.mentorEmail,
        mentorWorkPosition: modelStep2?.mentorWorkPosition,
        mentorCellPhone: modelStep2?.mentorCellPhone,

        //step 3
        requestTypeId: modelStep3?.requestTypeId,
        schoolData: modelStep3?.schoolIds.map((x) => ({ schoolId: +x })),
        schoolDependencies: modelStep3?.schoolDependencies,
        requestSolicitorId: modelStep3?.requestSolicitorId,
        requestSolicitorUniversityId: modelStep3?.university?.id || null,
        requestSolicitorDescription:
          modelStep3?.requestSolicitorId !== requestSchoolTypes.school
            ? modelStep3?.requestSolicitorDescription
            : modelStep3?.university?.name,

        //step 4
        investigationTitle: modelStep4?.investigationTitle,
        investigationObjective: modelStep4?.investigationObjective,
        investigationMethodology: modelStep4?.investigationMethodology,
        investigationSamplingDescription:
          modelStep4?.investigationSamplingDescription,

        //step 5
        investigationImportanceDescription:
          modelStep5?.investigationImportanceDescription,
        approvedParents: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsGuardian
        ),
        approvedDirectors: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsPrincipal
        ),
        approvedStudents: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsStudents
        ),
        approvedTeachers: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationImpactsTeachers
        ),
        approvedSharedTeachers: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationShareTeachers
        ),
        approvedGeneral: modelStep5?.selectedLetters.includes(
          attachmentCategories.InvestigationInpactsGeneral
        ),
      };

      const { data: saveResponse } = await _saveRequest(payload).catch(
        (error) => {
          this.ShowToast('', error.response.data.message, 'error');
        }
      );

      return saveResponse;
    },
    async onRefreshHandler() {
      await this.getRequestInfo();
    },
    async onDownloadRequestReportHandler() {
      if (this.businessId) {
        const response = await getInvestigationRequestReport(
          this.businessId,
          this.isEnLang
        ).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
        if (response?.data) {
          convertToBlob(response.data);
        }
      }
    },
    async onSubmitRequestHandler() {
      if (this.itemId) {
        const isValid = await this.validateAllSteps(false);
        if (!isValid) return;

        const answer = await this.ShowCancelModal(
          i18n.t('ucMessagebox.TitleText').toUpperCase(),
          i18n.t('question-save-modal-title'),
          i18n.t('modal-yes-text'),
          i18n.t('btnCancel.Text')
        );

        if (answer) {
          let saveRequest = await this.onSave();
          if (!saveRequest) return;

          const { data: saveResponse } = await _submitRequest(
            this.itemId
          ).catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });

          if (saveResponse) {
            this.ShowToast(
              i18n.t('ProcessSuccessfully'),
              i18n.t('success-modal-title'),
              'success'
            );
            this.tabId = 0; // Move to first tab
            await this.getRequestInfo();

            // Show rating survey
            this.onOpenRatingSurveyModal();
          }
        }
      }
    },
    async onRemoveRequestHandler() {
      if (this.itemId) {
        const answer = await this.ShowCancelModal(
          i18n.t('ucMessagebox.TitleText').toUpperCase(),
          i18n.t('RemoveRequest-question'),
          i18n.t('modal-yes-text'),
          i18n.t('btnCancel.Text')
        );

        if (answer) {
          const { data: saveRemove } = await _removeRequest(this.itemId).catch(
            (error) => {
              this.ShowToast('', error.response.data.message, 'error');
            }
          );

          if (saveRemove) {
            this.ShowToast(
              i18n.t('RemoveRequest-question-success'),
              i18n.t('success-modal-title'),
              'success'
            );
            this.tabId = 0; // Move to first tab
            //Move to dashboard
            this.$router.replace('/');
          }
        }
      }
    },
    onOpenRatingSurveyModal() {
      if (this.$refs.myRatingSurveyRef) {
        this.$refs.myRatingSurveyRef.showModal();
      }
    },
    onCloseRatingSurveyModal() {
      this.$refs.myRatingSurveyRef?.hideModal();
    },
    async onAcceptRatingSurveyHandler() {
      const value = this.$refs.myRatingSurveyRef.model.value ?? 0;

      //save rating survey
      await _saveRatingSurvey({ value });

      //close modal
      this.onCloseRatingSurveyModal();
    },

    //TODO: (Move the component and instance to the right place)
    onOpenGenerateReportModal() {
      if (this.$refs.generateReportModal) {
        this.$refs.generateReportModal.showModal();
      }
    },
    onCloseGenerateReportModal() {
      this.$refs.generateReportModal?.hideModal();
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';
@import '@/scss/bootstrap/_colors.scss';

.custom-tab__extended ::v-deep {
  .custom-tab-container {
    background: map-get($colors, 'white');
    border-bottom: unset !important;
    margin-left: unset !important;
    margin-right: unset !important;

    .nav-item {
      .nav-link {
        &.active {
          background: map-get($colors, 'gray-21');
          color: map-get($colors, 'blue-light-2') !important;
          border-bottom: unset !important;
          border-top: 3px solid map-get($colors, 'blue-light-2');
        }
      }
    }
  }
}

::v-deep button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.icon-view-size ::v-deep {
  width: auto !important;

  .dot {
    width: 35px !important;
    height: 35px !important;

    .title-icon {
      font-size: 15px !important;
    }
  }
}
</style>
